import { Button, Input, PageHeader, Switch } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { Title } from 'components/RevxTypography';
import { NetworkMember, NetworkPESwitchEnum } from 'domains/networkDomain';
import { PLATFORM_ORG_ID } from 'domains/orgDomain';
import { keyBy } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const MarketplaceNetworkMemberTable = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(
    (state) => state.loading.effects.network.getNetworksByMemberId
  );
  const orgsLoading = useAppSelector(
    (state) => state.loading.effects.org.getOrgs
  );
  const orgs = useAppSelector((state) => state.org.list);
  const allMembers = useAppSelector((state) => state.network.members);
  const forOrgId = useAppSelector(
    (state) => state.network.marketplaceNetworksForOrgId
  );
  const [searchValue, setSearchValue] = useState<string>('');

  const [allMembersHitMap, forOrgMembers, forOrgMembersHitMap] = useMemo(() => {
    const allMembersHitMap = keyBy(allMembers, 'memberId');

    const forOrgMembers = forOrgId
      ? allMembersHitMap[forOrgId].marketplaceNetworks
      : [];
    const forOrgMembersHitMap = keyBy(forOrgMembers, 'memberId');
    return [allMembersHitMap, forOrgMembers, forOrgMembersHitMap];
  }, [allMembers, forOrgId]);
  const orgHitMap = keyBy(orgs, 'id');

  const [batchLoading, setBatchLoading] = useState(false);
  const [forOrgMemberStatusFilter, setForOrgMemberStatusFilter] =
    useState<'yes' | 'no' | undefined>(undefined);

  const filteredMembers = useMemo(() => {
    return allMembers
      .filter((member) => {
        const notForOrg = member.memberId !== forOrgId;
        const org = orgHitMap[member.memberId];
        if (!org) return false;
        const isSearchHit = searchValue
          ? String(org.id) === searchValue ||
            org.name.toLowerCase().includes(searchValue.toLowerCase())
          : true;

        return (
          notForOrg &&
          isSearchHit &&
          (!Boolean(forOrgMemberStatusFilter) ||
            (forOrgMemberStatusFilter === 'yes' &&
              Boolean(forOrgMembersHitMap[member.memberId])) ||
            (forOrgMemberStatusFilter === 'no' &&
              !Boolean(forOrgMembersHitMap[member.memberId])))
        );
      })
      .map((member) => ({
        ...member,
        isInNetwork: Boolean(forOrgMembersHitMap[member.memberId]),
      }))
      .sort((a, b) => a.memberId - b.memberId);
  }, [
    allMembers,
    forOrgId,
    orgHitMap,
    searchValue,
    forOrgMemberStatusFilter,
    forOrgMembersHitMap,
  ]);

  const onChangeInNetwork = useCallback(
    async (checkedValue: boolean, org) => {
      if (!forOrgId) return;
      try {
        await dispatch.network.updateMember({
          ...allMembersHitMap[forOrgId],
          marketplaceNetworkOrgIds: checkedValue
            ? [...forOrgMembers.map((m) => m.memberId), org.memberId]
            : forOrgMembers
                .filter((m) => m.memberId !== org.memberId)
                .map((m) => m.memberId),
        });
      } catch (e) {}
      await dispatch.network.getNetworksByMemberId({
        networkId: PLATFORM_ORG_ID,
      });
    },
    [forOrgId, dispatch.network, allMembersHitMap, forOrgMembers]
  );

  const onSelectAll = useCallback(async () => {
    if (!forOrgId) return;
    setBatchLoading(true);
    try {
      const supplierOrgIds = allMembers
        .filter(
          (member) =>
            member.memberId !== forOrgId &&
            orgHitMap[member.memberId]?.isSupplier &&
            member.peswitch !== NetworkPESwitchEnum.PRIVATE
        )
        .map((member) => member.memberId);
      await dispatch.network.updateMember({
        ...allMembersHitMap[forOrgId],
        marketplaceNetworkOrgIds: supplierOrgIds,
      });
      await dispatch.network.getNetworksByMemberId({
        networkId: PLATFORM_ORG_ID,
      });
    } catch (e) {}
    setBatchLoading(false);
  }, [forOrgId, dispatch.network, allMembersHitMap, allMembers, orgHitMap]);

  const onDeselectAll = useCallback(async () => {
    if (!forOrgId) return;
    setBatchLoading(true);
    try {
      await dispatch.network.updateMember({
        ...allMembersHitMap[forOrgId],
        marketplaceNetworkOrgIds: [],
      });
      await dispatch.network.getNetworksByMemberId({
        networkId: PLATFORM_ORG_ID,
      });
    } catch (e) {}
    setBatchLoading(false);
  }, [forOrgId, dispatch.network, allMembersHitMap]);

  const columns: TableColumnsType<NetworkMember> = [
    {
      title: t('common.id'),
      width: 100,
      dataIndex: 'memberId',
    },
    {
      title: t('common.name'),
      dataIndex: 'memberId',
      render: (value) => {
        return orgHitMap[value]?.name;
      },
    },
    {
      title: t('platform.org.table.columns.tags'),
      dataIndex: 'memberId',
      render: (value) => {
        return orgHitMap[value]?.tags;
      },
    },
    {
      title: t('platform.org.table.columns.city'),
      width: 100,
      dataIndex: 'memberId',
      render: (value) => {
        return orgHitMap[value]?.city;
      },
    },
    {
      title: (
        <div>
          <div>Marketplace Member</div>
          <div style={{ marginTop: 4, whiteSpace: 'nowrap' }}>
            <Button type="link" size="small" disabled={batchLoading} onClick={onSelectAll}>
              Select All
            </Button>
            <Button type="link" size="small" disabled={batchLoading} onClick={onDeselectAll}>
              Deselect All
            </Button>
          </div>
        </div>
      ),
      width: 220,
      dataIndex: 'isInNetwork',
      filterMultiple: false,
      filteredValue: forOrgMemberStatusFilter
        ? [forOrgMemberStatusFilter]
        : undefined,
      filters: [
        {
          text: t('platform.network.active'),
          value: 'yes',
        },
        {
          text: t('platform.network.inactive'),
          value: 'no',
        },
      ],

      render: (value, record) => (
        <Switch
          disabled={
            record.peswitch === NetworkPESwitchEnum.PRIVATE ||
            !orgHitMap[record.memberId].isSupplier
          }
          checked={value}
          onClick={(c, e) => e.stopPropagation()}
          onChange={(checked) => onChangeInNetwork(checked, record)}
        />
      ),
    },
    {
      title: t('platform.network.pricingData'),
      dataIndex: 'peswitch',
      render: (value) => {
        return value === NetworkPESwitchEnum.LIVE ? (
          <>{t('common.live')}</>
        ) : value === NetworkPESwitchEnum.PRIVATE ? (
          <>{t('common.private')}</>
        ) : (
          <></>
        );
      },
    },
    {
      title: 'Is Supplier',
      dataIndex: 'memberId',
      render: (value) => {
        return orgHitMap[value].isSupplier ? (
          <>{t('common.yes')}</>
        ) : (
          <>{t('common.no')}</>
        );
      },
    },
  ];

  const onTableChange = useCallback((pagination, filters, sorter, extra) => {
    switch (extra.action) {
      case 'filter':
        if (filters.isInNetwork) {
          setForOrgMemberStatusFilter(filters.isInNetwork[0] as 'yes' | 'no');
        } else {
          setForOrgMemberStatusFilter(undefined);
        }

        break;
    }
  }, []);

  console.log('filteredMembers', filteredMembers);
  console.log('allMembers', allMembers);
  console.log('forOrgMemberStatusFilter', forOrgMemberStatusFilter);

  return (
    <>
      <PageHeader
        ghost={false}
        className="platform-page-header"
        title={
          <Title style={{ marginBottom: 0, marginTop: 6, marginRight: 40 }}>
            {forOrgMembers.length} Marketplace Network Members
          </Title>
        }
        extra={[
          <Input.Search
            key="search"
            placeholder={t('platform.network.searchPlaceholder')}
            onChange={(e) => setSearchValue(e.target.value)}
            allowClear
            enterButton
            style={{ width: 400 }}
          />,
        ]}
      />
      <RevxTable<NetworkMember>
        loading={loading || orgsLoading || batchLoading}
        columns={columns}
        pagination={false}
        dataSource={filteredMembers}
        onChange={onTableChange}
      />
    </>
  );
};
